<template>
  <div class="text-stone-700 flex flex-col gap-4">
    <!-- Gift card section -->
    <ElevatedCard v-if="showGiftcard" variant="teal">
      <span
        class="absolute top-0 right-3 -translate-y-1/2 bg-teal-500 text-white text-xs font-semibold px-2.5 py-0.5 rounded-full"
      >
        {{ t('product.show.noDates.giftCard.badge') }}
      </span>

      <div class="flex items-start justify-between gap-2">
        <div>
          <span class="font-bold text-body-xl leading-snug">
            {{ t('product.show.noDates.giftCard.title') }}
          </span>
          <p class="text-sm text-gray-500 mt-0.5">
            {{ t('product.show.noDates.giftCard.subtitle') }}
          </p>
        </div>
        <span class="font-bold text-body-xl shrink-0">{{
          formattedPrice
        }}</span>
      </div>

      <div class="mt-3 bg-teal-50 border border-teal-200 rounded-2xl p-3">
        <div
          class="flex items-center gap-1.5 text-xs font-semibold text-teal-700 uppercase tracking-wide mb-2"
        >
          <Gift class="w-3.5 h-3.5" />
          {{ t('product.show.noDates.giftCard.sectionLabel') }}
        </div>
        <ul class="flex flex-col gap-1.5 text-xs text-gray-700">
          <li class="flex gap-2">
            <Check class="w-4 h-4 text-teal-600 shrink-0" />
            {{ t('product.show.noDates.giftCard.bullet1') }}
          </li>
          <li class="flex gap-2">
            <Check class="w-4 h-4 text-teal-600 shrink-0" />
            {{ t('product.show.noDates.giftCard.bullet2') }}
          </li>
          <li class="flex gap-2">
            <Check class="w-4 h-4 text-teal-600 shrink-0" />
            {{ t('product.show.noDates.giftCard.bullet3') }}
          </li>
        </ul>
      </div>

      <Button
        data-test-id="buy-box-no-dates-giftcard-cta"
        class="w-full mt-4 py-3 rounded-xl"
        :style="buttonStyle"
        @click="buyGiftcard"
      >
        {{ t('product.show.checkout.bookGiftcard') }}
      </Button>
    </ElevatedCard>

    <!-- OR divider -->
    <EventBuyBoxOrDivider
      v-if="showGiftcard && (showDateRequest || showWaitlist || isEmbedded)"
      :class="isKitsPage ? '-order-1' : undefined"
    />

    <!-- Request a date section -->
    <EventBuyBoxRequestCard
      v-if="showDateRequest"
      compact
      :title="t('product.show.noDates.dateRequest.title')"
      :badge="t('product.show.noDates.dateRequest.badge')"
      :description="t('product.show.noDates.dateRequest.description')"
    >
      <Button
        data-test-id="buy-box-no-dates-request-date-button"
        variant="outlined"
        class="w-full py-3 flex items-center justify-center gap-2 border-2 font-semibold rounded-xl"
        @click="requestDate"
      >
        <Calendar class="w-4 h-4" />
        {{ t('product.show.noAvailableDatesBox.ctaTextOnlyProposeDate') }}
      </Button>
    </EventBuyBoxRequestCard>

    <!-- Notify section -->
    <EventBuyBoxNotifyNewDates
      v-if="showWaitlist"
      :is-kits-page="isKitsPage"
      :formatted-price="formattedPrice"
    />

    <!-- embedded fallback -->
    <EventBuyBoxRequestCard
      v-if="isEmbedded"
      compact
      :title="
        isKitsPage
          ? t('product.show.noInventory.embedded.title')
          : t('product.show.noDates.embedded.title')
      "
      :badge="
        isKitsPage
          ? t('product.show.noInventory.embedded.badge')
          : t('product.show.noDates.embedded.badge')
      "
      :description="
        isKitsPage
          ? t('product.show.noInventory.embedded.description')
          : t('product.show.noDates.embedded.description')
      "
    >
      <Button
        v-if="partnerContactEmail"
        data-test-id="buy-box-no-dates-contact-us-button"
        variant="outlined"
        class="w-full py-3 flex items-center justify-center gap-2 border-2 font-semibold rounded-xl"
        :link="partnerContactEmail ? `mailto:${partnerContactEmail}` : ''"
      >
        {{
          isKitsPage
            ? t('product.show.noInventory.embedded.CTALabel')
            : t('product.show.noDates.embedded.CTALabel')
        }}
      </Button>
    </EventBuyBoxRequestCard>
  </div>
</template>

<script setup lang="ts">
import { Calendar, Check, Gift } from 'lucide-vue-next'
import parseGiftcardToCartItem from '~/utils/parseGiftcardToCartItem'
import type { CartItem } from '~/composables/types/cart'
import type { EventDescription, Supplier } from '~/composables/types/event'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'

const { showGiftcard, isEmbedded, isKitsPage, backgroundColor, textColor } =
  defineProps<{
    showGiftcard: boolean
    showDateRequest?: boolean
    showWaitlist?: boolean
    isEmbedded?: boolean
    isKitsPage?: boolean
    backgroundColor?: string
    textColor?: string
  }>()

const emit = defineEmits<{
  (e: 'add-item-to-cart', cartItem: CartItem): void
}>()

const eventData = inject<EventDescription>('eventData')!
const isSpecialGiftcardEvent = inject('isSpecialGiftcardEvent') as boolean
const { pricingsFactory } = usePricingsFactory()
const pricings = pricingsFactory(
  Number(
    eventData.retail_price?.amount ||
      eventData.original_price?.amount ||
      eventData.default_price.amount
  ),
  { totalParticipants: 1 }
)
const { locale, t } = useI18n()
const route = useRoute()
const { addItem, clear: clearCart } = useCartWithSubdomainLogic()
const { toggleCartSidebar, toggleDateRequestSidebar } = useUiState()
const { sendAddToCartEvent } = useAlgoliaConversion()

const formattedPrice = computed(() => {
  const amount = Number(
    eventData.retail_price?.amount ||
      eventData.original_price?.amount ||
      eventData.default_price.amount
  )
  return formatMoney(
    amount / 100,
    convertLocaleIntoBCP47(locale.value),
    'EUR',
    Number.isInteger(amount / 100) ? 0 : 2
  )
})

const buttonStyle = computed(() => ({
  ...(textColor ? { color: `#${textColor}` } : {}),
  ...(backgroundColor ? { backgroundColor: `#${backgroundColor}` } : {}),
}))

const buyGiftcard = async () => {
  const cartItem = parseGiftcardToCartItem(
    eventData,
    1,
    ref(undefined),
    pricings,
    isSpecialGiftcardEvent ? 'SPECIAL' : 'EVENT'
  )
  if (cartItem) {
    emit('add-item-to-cart', cartItem)
    if (isEmbedded) clearCart()
    await addItem(cartItem)
    toggleCartSidebar()
    sendAddToCartEvent(eventData)
  }
}

const requestDate = () => {
  toggleDateRequestSidebar(route.name, 'buybox_no_dates')
}

const partnerContactEmail = ref('')

const fetchPartnerContactEmail = async () => {
  const partnerPermalink = supplierGetters.getPermalink(
    eventGetters.getSupplier(eventData)
  )
  if (!partnerPermalink || !isEmbedded) return
  try {
    const response = await kftApiRequest<Supplier>(
      `v1/store/suppliers/${partnerPermalink}/?include=booking_solution_settings`,
      { method: 'GET' }
    )
    partnerContactEmail.value = supplierGetters.getContactEmail(response?.data)
  } catch (error) {
    console.error('Failed to fetch partner contact email:', error)
  }
}

onMounted(() => {
  if (isEmbedded) fetchPartnerContactEmail()
})
</script>
